import React, { useState } from 'react';
import { X, Send, Check, ChevronDown } from 'lucide-react';

const ORDER_PROCESS_STEPS = [
  { title: 'Design Consultation', body: 'A private conversation \u2014 over WhatsApp, video call, or in our Lucknow studio \u2014 to understand the moment, the silhouette, and the sentiment behind your piece.' },
  { title: 'Order Confirmation', body: 'Once design, fabric and embroidery are finalised, we share a confirmation note with your bespoke specifications, timeline and pricing.' },
  { title: '50% Advance Payment', body: 'A 50% advance secures your slot on our atelier calendar and releases fabric and karigar resources to begin work.' },
  { title: 'Handcrafting Process', body: 'Master karigars commence the slow art of cutting, embroidering and finishing your piece. Most commissions take six to eight weeks.' },
  { title: 'Final Preview', body: 'Photographs and a short video for your review. Minor refinements are gladly accommodated before final dispatch.' },
  { title: 'Balance Payment', body: 'The remaining 50% is settled before delivery. Bank transfer, UPI and international wire accepted.' },
  { title: 'Delivery', body: 'Your commission is hand-packed in our heritage box with a hand-written note and dispatched via insured courier with full tracking.' },
];

const InquiryForm = ({ isOpen, onClose, selectedPiece }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [processOpen, setProcessOpen] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          pieceId: selectedPiece?.id || null,
          pieceName: selectedPiece?.name || null,
          source: selectedPiece ? 'piece_inquiry' : 'contact_form'
        }),
      });

      if (!response.ok) {
        let serverMsg = '';
        try {
          const errBody = await response.json();
          if (typeof errBody?.detail === 'string') serverMsg = errBody.detail;
          else if (Array.isArray(errBody?.detail)) {
            serverMsg = errBody.detail.map((e) => e?.msg || '').filter(Boolean).join(' • ');
          }
        } catch { /* ignore parse error, fallback below */ }
        throw new Error(serverMsg || 'Unable to submit inquiry. Please try again or contact us via WhatsApp.');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      setTimeout(() => {
        setIsSubmitted(false);
        onClose();
      }, 3000);
    } catch (err) {
      setError(err?.message
        ? `${err.message}`
        : 'Unable to submit inquiry. Please try again or contact us via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setFormData({ name: '', email: '', phone: '', message: '' });
    setError('');
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={resetAndClose} data-testid="inquiry-modal">
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button 
          style={styles.closeButton} 
          onClick={resetAndClose}
          aria-label="Close"
          data-testid="inquiry-close-btn"
        >
          <X size={24} />
        </button>

        {isSubmitted ? (
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>
              <Check size={32} color="var(--accent-gold)" />
            </div>
            <h3 className="heading-2">Thank You</h3>
            <p style={styles.successText}>
              Your inquiry has been received. We shall connect with you shortly.
            </p>
          </div>
        ) : (
          <>
            <div style={styles.header}>
              <p style={styles.headerLabel}>Begin Your Journey</p>
              <h2 className="heading-2">
                {selectedPiece ? 'Inquire About This Piece' : 'Send an Inquiry'}
              </h2>
            </div>

            {selectedPiece && (
              <div style={styles.pieceInfo}>
                <span style={styles.pieceName}>{selectedPiece.name}</span>
                {selectedPiece.technique && (
                  <span style={styles.pieceTechnique}>{selectedPiece.technique}</span>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="Your name"
                  data-testid="inquiry-name-input"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="your@email.com"
                  data-testid="inquiry-email-input"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="inquiry-phone-input"
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  style={styles.textarea}
                  placeholder="Tell us about your vision..."
                  data-testid="inquiry-message-input"
                />
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div className="inquiry-policy-note" data-testid="inquiry-policy-note">
                <p className="inquiry-policy-note__line">
                  All pieces are <strong>made-to-order</strong>. A 50% advance confirms your commission.
                </p>
                <button
                  type="button"
                  className={`inquiry-process-toggle ${processOpen ? 'is-open' : ''}`}
                  onClick={() => setProcessOpen((v) => !v)}
                  aria-expanded={processOpen}
                  aria-controls="inquiry-process-panel"
                  data-testid="inquiry-process-toggle"
                >
                  <span>{processOpen ? 'Hide order process' : 'View order process'}</span>
                  <ChevronDown size={14} strokeWidth={1.6} />
                </button>
                <div
                  id="inquiry-process-panel"
                  className={`inquiry-process-panel ${processOpen ? 'is-open' : ''}`}
                  role="region"
                  aria-hidden={!processOpen}
                  data-testid="inquiry-process-panel"
                >
                  <ol className="inquiry-process-list">
                    {ORDER_PROCESS_STEPS.map((s, i) => (
                      <li key={s.title} className="inquiry-process-item" data-testid={`inquiry-process-step-${i + 1}`}>
                        <span className="inquiry-process-num">{String(i + 1).padStart(2, '0')}</span>
                        <div>
                          <h5 className="inquiry-process-title">{s.title}</h5>
                          <p className="inquiry-process-body">{s.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <button 
                type="submit" 
                className="btn-primary"
                style={styles.submitButton}
                disabled={isSubmitting}
                data-testid="inquiry-submit-btn"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send size={16} />
                    Send Inquiry
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    animation: 'fadeIn 0.3s ease',
  },
  modal: {
    background: 'var(--bg-primary)',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '48px',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    padding: '8px',
    transition: 'color 0.3s ease',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  headerLabel: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '10px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '12px',
  },
  pieceInfo: {
    background: 'var(--bg-secondary)',
    padding: '16px 20px',
    marginBottom: '24px',
    textAlign: 'center',
  },
  pieceName: {
    display: 'block',
    fontFamily: "'Playfair Display', serif",
    fontSize: '16px',
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },
  pieceTechnique: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '10px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
  },
  input: {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-light)',
    padding: '14px 16px',
    color: 'var(--text-primary)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    transition: 'border-color 0.3s ease',
    outline: 'none',
  },
  textarea: {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-light)',
    padding: '14px 16px',
    color: 'var(--text-primary)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '100px',
    transition: 'border-color 0.3s ease',
    outline: 'none',
  },
  error: {
    color: '#c9616a',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    textAlign: 'center',
  },
  submitButton: {
    width: '100%',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  successIcon: {
    width: '72px',
    height: '72px',
    border: '2px solid var(--accent-gold)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  successText: {
    color: 'var(--text-secondary)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    lineHeight: '1.6',
    marginTop: '16px',
  },
};

// Add focus styles
const focusStyles = document.createElement('style');
focusStyles.textContent = `
  [data-testid="inquiry-modal"] input:focus,
  [data-testid="inquiry-modal"] textarea:focus {
    border-color: var(--accent-gold) !important;
  }
  [data-testid="inquiry-close-btn"]:hover {
    color: var(--text-primary) !important;
  }
`;
document.head.appendChild(focusStyles);

export default InquiryForm;
